import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Auto-playing photo carousel for the "What we clean" section.
 * - 4s per slide, pauses on hover.
 * - Manual prev/next + pagination dots.
 * Each slide: { image, title, desc }
 */
export default function WhatWeCleanCarousel({ slides = [] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const count = slides.length;

  const go = useCallback((next) => {
    setIndex((i) => ((next % count) + count) % count);
  }, [count]);

  useEffect(() => {
    if (paused || count <= 1) return;
    timer.current = setTimeout(() => setIndex((i) => (i + 1) % count), 4000);
    return () => clearTimeout(timer.current);
  }, [index, paused, count]);

  if (count === 0) return null;
  const slide = slides[index];

  return (
    <div
      className="relative rounded-[2rem] overflow-hidden border border-[#E5E7EB] shadow-xl bg-[#0C1A1A]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="what-we-clean-carousel"
    >
      <div className="relative aspect-[16/10] sm:aspect-[16/9]">
        <AnimatePresence mode="popLayout">
          <motion.img
            key={index}
            src={slide.image}
            alt={slide.title}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />
        </AnimatePresence>
        {/* gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0C1A1A]/90 via-[#0C1A1A]/25 to-transparent" />

        {/* caption */}
        <div className="absolute left-0 right-0 bottom-0 p-6 md:p-9">
          <AnimatePresence mode="wait">
            <motion.div
              key={`cap-${index}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4 }}
              data-testid={`what-we-clean-slide-${index}`}
            >
              <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-[#FACC15] text-[#0C1A1A] text-[11px] font-bold uppercase tracking-wider mb-3">
                {String(index + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-extrabold tracking-tighter text-white">{slide.title}</h3>
              <p className="mt-1.5 text-sm md:text-base text-white/80 max-w-lg leading-relaxed">{slide.desc}</p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* arrows */}
        <button
          onClick={() => go(index - 1)}
          aria-label="Previous"
          className="absolute top-1/2 left-3 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white text-[#0C1A1A] grid place-items-center shadow-lg backdrop-blur transition-colors"
          data-testid="what-we-clean-prev"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={() => go(index + 1)}
          aria-label="Next"
          className="absolute top-1/2 right-3 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white text-[#0C1A1A] grid place-items-center shadow-lg backdrop-blur transition-colors"
          data-testid="what-we-clean-next"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* dots */}
      <div className="absolute top-4 right-4 flex gap-1.5" data-testid="what-we-clean-dots">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${i === index ? "w-6 bg-[#FACC15]" : "w-1.5 bg-white/50 hover:bg-white/80"}`}
            data-testid={`what-we-clean-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}
